import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Undo2, Pencil, Check, Trash2 } from 'lucide-react';
import { useEditDocument, EditabilityBanner, saveErrorMessage } from './documentEdit';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';

// New Estimate / New Return Authorization (Native) — one shared component
// (kind 'estimate' | 'rma'), same pattern as NativeEntityNew. Both documents
// are NON-POSTING (no GL, no inventory movement): the native row is the
// record; NetSuite gets a best-effort copy with a retry path server-side.
//
// EDIT MODE: with an :id in the route the same screen loads that document,
// prefills itself, and saves with PATCH. Nothing downstream draws against a
// quote or an RMA, so while the document is open every field is editable and
// the lines are simply replaced. The one hard stop is a NetSuite copy: the NS
// leg is fire-once with no update path, so a synced document must be voided
// and re-created instead — the server says so (nativeDocEdit.js) and the
// verdict arrives as `editability`.

const CFG = {
  estimate: {
    title: 'New Estimate (Native)',
    icon: FileText,
    api: '/api/native-estimates/estimates',
    numberKey: 'estimate_number',
    hasExpires: true,
    hasCreatedFrom: false,
    nsNoun: 'estimate',
  },
  rma: {
    title: 'New Return Authorization (Native)',
    icon: Undo2,
    api: '/api/native-estimates/return-authorizations',
    numberKey: 'rma_number',
    hasExpires: false,
    hasCreatedFrom: true,
    nsNoun: 'return authorization',
  },
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function NativeEstimateNew({ kind }) {
  const cfg = CFG[kind];
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const Icon = isEdit ? Pencil : cfg.icon;

  const [customer, setCustomer] = useState(null);
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);

  const [itemQuery, setItemQuery] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [lines, setLines] = useState([]);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiresDate, setExpiresDate] = useState('');
  const [createdFromSo, setCreatedFromSo] = useState('');
  const [memo, setMemo] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  // sourceKey fixed for this screen's lifetime — double-clicking Post can't
  // create two documents (server is idempotent on it).
  const [sourceKey] = useState(() => `${kind === 'rma' ? 'RMA' : 'EST'}-UI-${Date.now()}-${Math.floor(Math.random() * 1e6)}`);

  // ── edit mode ──────────────────────────────────────────────────────────────
  const [editability, setEditability] = useState(null);
  const [docNumber, setDocNumber] = useState(null);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(undefined);
  const loaded = useEditDocument(isEdit ? `${cfg.api}/${encodeURIComponent(editId)}` : null, (d) => d);

  useEffect(() => {
    const payload = loaded.doc;
    if (!payload || !payload.document) return;
    const d = payload.document;
    setExpectedUpdatedAt(d.updated_at ?? null);
    setEditability(loaded.editability);
    setDocNumber(d[cfg.numberKey]);
    setCustomer({ nsCustomerId: d.ns_customer_id, name: d.customer_name, email: null });
    setLines((payload.lines || []).map((l) => ({
      sku: l.sku,
      name: l.description || l.sku,
      quantity: Number(l.quantity),
      rate: Number(l.rate),
    })));
    setDate(d[kind === 'rma' ? 'rma_date' : 'estimate_date'] || d.created_at?.slice(0, 10) || '');
    if (cfg.hasExpires) setExpiresDate(d.expires_date || '');
    if (cfg.hasCreatedFrom) setCreatedFromSo(d.created_from_ns_so_id != null ? String(d.created_from_ns_so_id) : '');
    setMemo(d.memo || '');
  }, [loaded.doc, loaded.editability, cfg, kind]);

  const locked = isEdit && editability != null && !editability.editable;

  // customer search (debounced)
  useEffect(() => {
    if (customer || custQuery.trim().length < 2) { setCustResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-orders/customers', { params: { q: custQuery.trim() } });
        setCustResults(data.customers || []);
      } catch { setCustResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, customer]);

  // item search (debounced)
  useEffect(() => {
    if (itemQuery.trim().length < 2) { setItemResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-orders/items', { params: { q: itemQuery.trim() } });
        setItemResults(data.items || []);
      } catch { setItemResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [itemQuery]);

  const addItem = (it) => {
    setLines((prev) => {
      if (prev.some((l) => l.sku === it.sku)) return prev;
      return [...prev, { sku: it.sku, name: it.name, quantity: 1, rate: it.price != null ? it.price : 0 }];
    });
    setItemQuery('');
    setItemResults([]);
  };
  const setLine = (i, key, value) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + (Number(l.quantity) > 0 ? Number(l.quantity) : 0) * (Number(l.rate) >= 0 ? Number(l.rate) : 0), 0);
  const linesValid = lines.length > 0 && lines.every((l) => Number(l.quantity) > 0 && Number(l.rate) >= 0 && Number.isFinite(Number(l.rate)));
  const canSubmit = customer && linesValid && !submitting && !locked;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        sourceKey,
        customer: { nsId: customer.nsCustomerId },
        lines: lines.map((l) => ({ itemIdOrSku: l.sku, quantity: Number(l.quantity), rate: Number(l.rate) })),
        date,
        memo: memo.trim() || undefined,
      };
      if (cfg.hasExpires && expiresDate) body.expiresDate = expiresDate;
      if (cfg.hasCreatedFrom && createdFromSo.trim()) body.createdFromNsSoId = createdFromSo.trim();
      const { data } = isEdit
        ? await axios.patch(`${cfg.api}/${encodeURIComponent(editId)}`, { ...body, expectedUpdatedAt })
        : await axios.post(cfg.api, body);
      if (isEdit) {
        // Re-arm the compare-and-swap so a second save from this screen works.
        setExpectedUpdatedAt(data.document?.updated_at ?? null);
        setEditability(data.editability || editability);
      }
      setConfirmation(data);
    } catch (err) {
      setError(saveErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const d = confirmation.document || {};
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{d[cfg.numberKey]} {isEdit ? 'updated' : 'created'}</h2>
          <div className="nia-confirm-grid">
            <span>Customer</span><strong>{d.customer_name}</strong>
            <span>Total</span><strong>{fmtMoney(d.total)}</strong>
            <span>NetSuite</span>
            <strong>
              {d.ns_status === 'synced'
                ? `${cfg.nsNoun} ${d.ns_transaction_id} created`
                : d.ns_status === 'skipped'
                  ? 'no NS copy (native record is the book)'
                  : `pending — ${d.ns_error || 'will retry'}`}
            </strong>
          </div>
          <p className="nia-hint">Non-posting document: no GL entry, no inventory movement.</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>
              {isEdit ? 'Back to the document' : `New ${kind === 'rma' ? 'RMA' : 'estimate'}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Icon size={20} /> {isEdit ? `Edit ${docNumber || cfg.nsNoun}` : cfg.title}</h1>
      </div>
      {loaded.loading && <div className="nia-card"><p className="nia-hint" style={{ margin: 0 }}>Loading the {cfg.nsNoun}…</p></div>}
      {loaded.error && <div className="nia-error">{loaded.error}</div>}
      <EditabilityBanner editability={editability} prefix="nia" />

      <div className="nia-card">
        <h3>1. Customer</h3>
        {!customer ? (
          <>
            <input
              className="nia-input nia-input-search"
              placeholder="Search customers by name or email…"
              value={custQuery}
              onChange={(e) => setCustQuery(e.target.value)}
            />
            {custResults.length > 0 && (
              <div className="nia-results">
                {custResults.map((c) => (
                  <button key={c.nsCustomerId} className="nia-result" onClick={() => { setCustomer(c); setCustQuery(''); setCustResults([]); }}>
                    <strong>{c.name}</strong>{c.email && <em>{c.email}</em>}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            {customer.name}
            {!locked && <button className="nia-icon-btn" onClick={() => setCustomer(null)}>×</button>}
          </div>
        )}
      </div>

      <div className="nia-card">
        <h3>2. Items</h3>
        <input
          className="nia-input nia-input-search"
          placeholder="Search items by SKU or name…"
          value={itemQuery}
          onChange={(e) => setItemQuery(e.target.value)}
        />
        {itemResults.length > 0 && (
          <div className="nia-results">
            {itemResults.map((it) => (
              <button key={it.sku} className="nia-result" onClick={() => addItem(it)}>
                <strong>{it.sku}</strong> {it.name}
                {it.price != null && <em>{fmtMoney(it.price)}</em>}
              </button>
            ))}
          </div>
        )}
        {lines.length > 0 && (
          <table className="nia-table">
            <thead>
              <tr><th>SKU</th><th>Item</th><th>Qty</th><th>Rate</th><th>Amount</th><th /></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.sku}>
                  <td>{l.sku}</td>
                  <td>{l.name}</td>
                  <td>
                    <input className="nia-input nia-input-num" type="number" min="0.0001" step="1" value={l.quantity}
                      onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                  </td>
                  <td>
                    <input className="nia-input nia-input-num" type="number" min="0" step="0.01" value={l.rate}
                      onChange={(e) => setLine(i, 'rate', e.target.value)} />
                  </td>
                  <td>{fmtMoney((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</td>
                  <td>
                    <button className="nia-icon-btn" onClick={() => removeLine(i)} title="Remove line"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {lines.length > 0 && (
          <div className="nia-totals"><span className="nia-total-line">Total {fmtMoney(total)}</span></div>
        )}
      </div>

      <div className="nia-card">
        <h3>3. Details</h3>
        <div className="nia-review-row">
          <label>
            <span>Date</span>
            <input className="nia-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          {cfg.hasExpires && (
            <label>
              <span>Expires (optional)</span>
              <input className="nia-input" type="date" value={expiresDate} onChange={(e) => setExpiresDate(e.target.value)} />
            </label>
          )}
          {cfg.hasCreatedFrom && (
            <label>
              <span>From NetSuite SO # (optional)</span>
              <input className="nia-input" placeholder="NS sales order internal id" value={createdFromSo}
                onChange={(e) => setCreatedFromSo(e.target.value.replace(/\D/g, ''))} maxLength={12} />
            </label>
          )}
          <label>
            <span>Memo (optional)</span>
            <input className="nia-input" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
          </label>
        </div>
        {error && <div className="nia-error" style={{ marginTop: 10 }}>{error}</div>}
        <div className="nia-actions" style={{ marginTop: 12 }}>
          <button className="nia-btn nia-btn-primary" onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : (isEdit ? 'Save changes' : `Create ${kind === 'rma' ? 'return authorization' : 'estimate'}`)}
          </button>
        </div>
        <p className="nia-hint" style={{ marginTop: 8 }}>
          {isEdit
            ? 'Non-posting document — nothing draws against it, so an edit is a straight data change with no reversal. The NetSuite copy is created separately with Retry NetSuite.'
            : 'Non-posting document — no GL entry and no inventory movement. Saves natively first, then copies to NetSuite (best-effort with retry).'}
        </p>
      </div>
    </div>
  );
}
